import { useState, useEffect } from 'react';
import { X, CreditCard, Wallet, Smartphone, Building2, Gift, Clock, AlertCircle, Check, Edit2, Scissors, Euro } from 'lucide-react';
import { QueueTicket, LoyaltyReward } from '../../types';
import { Button } from '../ui/Button';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../lib/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

interface PaymentModalProps {
  ticket: QueueTicket;
  onClose: () => void;
  onSuccess: () => void;
}

type PaymentMethod = 'card' | 'cash' | 'transfer' | 'bizum';

export default function PaymentModal({ ticket, onClose, onSuccess }: PaymentModalProps) {
  // Debug: Log ticket info immediately
  console.log('💳 [PaymentModal] Opened with ticket:', {
    queueId: ticket.queueId,
    userId: ticket.userId,
    clientName: ticket.clientName,
    clientEmail: ticket.clientEmail,
    serviceId: ticket.serviceId,
    status: ticket.status,
    fullTicket: ticket,
  });

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingDuration, setIsEditingDuration] = useState(false);

  // Voucher-related state
  const [availableRewards, setAvailableRewards] = useState<LoyaltyReward[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null);
  const [applyVoucher, setApplyVoucher] = useState(false);

  // Service price state (loaded from services collection)
  const [servicePrice, setServicePrice] = useState(0);
  const [originalServicePrice, setOriginalServicePrice] = useState(0);
  const [serviceName, setServiceName] = useState('');
  const [loadingServicePrice, setLoadingServicePrice] = useState(true);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [customPriceInput, setCustomPriceInput] = useState('');
  // Track if user explicitly saved a modified price
  const [priceExplicitlySaved, setPriceExplicitlySaved] = useState(false);

  // Track if price was modified (requires note) - only if explicitly saved by user
  const priceWasModified = priceExplicitlySaved && servicePrice !== originalServicePrice;

  // Read reward value dynamically from selected reward (with backward compatibility)
  const selectedReward = availableRewards.find(r => r.rewardId === selectedRewardId);
  const voucherValue = selectedReward
    ? (selectedReward as any).discountType === 'percentage' && (selectedReward as any).discountPercentage
      ? Math.round(servicePrice * (selectedReward as any).discountPercentage / 100)
      : ((selectedReward as any).valueCents || (selectedReward as any).value || ((selectedReward as any).valueInEuros ? (selectedReward as any).valueInEuros * 100 : 1000))
    : 1000;
  const discountAmount = applyVoucher && selectedRewardId ? Math.min(voucherValue, servicePrice) : 0;
  const remainingAmount = Math.max(0, servicePrice - discountAmount);
  const voucherCoversAll = discountAmount >= servicePrice;

  // Load service price from services collection and calculate duration
  useEffect(() => {
    async function loadServicePrice() {
      if (!ticket.serviceId) {
        console.warn('⚠️ [PaymentModal] No serviceId in ticket!');
        setLoadingServicePrice(false);
        return;
      }

      try {
        console.log('💰 [PaymentModal] Loading service price for serviceId:', ticket.serviceId);

        const serviceRef = doc(db, 'services', ticket.serviceId);
        const serviceDoc = await getDoc(serviceRef);

        if (serviceDoc.exists()) {
          const serviceData = serviceDoc.data();
          const priceInCents = serviceData.price || 0;
          console.log('💰 [PaymentModal] Service price:', priceInCents, 'cents (', (priceInCents / 100).toFixed(2), '€)');
          console.log('✂️ [PaymentModal] Service name:', serviceData.name);
          setServicePrice(priceInCents);
          setOriginalServicePrice(priceInCents);
          setServiceName(serviceData.name || 'Servicio');
          setCustomPriceInput((priceInCents / 100).toFixed(2));

          // Calculate actual service duration if not already set
          if (!duration) {
            let calculatedDuration = 30; // Default fallback

            // Priority 1: Calculate from actual service start time
            if (ticket.serviceStartedAt) {
              const startTime = ticket.serviceStartedAt.toDate().getTime();
              const endTime = ticket.completedAt
                ? ticket.completedAt.toDate().getTime()
                : Date.now();
              calculatedDuration = Math.round((endTime - startTime) / (1000 * 60)); // Convert to minutes
              console.log('⏱️ [PaymentModal] Calculated duration from serviceStartedAt:', calculatedDuration, 'min');
            }
            // Priority 2: Use service duration from ticket
            else if (ticket.serviceDuration) {
              calculatedDuration = ticket.serviceDuration;
              console.log('⏱️ [PaymentModal] Using ticket serviceDuration:', calculatedDuration, 'min');
            }
            // Priority 3: Use service duration from service data
            else if (serviceData.duration) {
              calculatedDuration = serviceData.duration;
              console.log('⏱️ [PaymentModal] Using service data duration:', calculatedDuration, 'min');
            }

            setDuration(calculatedDuration.toString());
          }
        } else {
          console.error('❌ [PaymentModal] Service not found:', ticket.serviceId);
        }
      } catch (err) {
        console.error('❌ [PaymentModal] Error loading service price:', err);
      } finally {
        setLoadingServicePrice(false);
      }
    }

    loadServicePrice();
  }, [ticket.serviceId, ticket.serviceStartedAt, ticket.completedAt, ticket.serviceDuration]);

  // Load available rewards for the client
  useEffect(() => {
    async function loadClientRewards() {
      console.log('🎁 [PaymentModal] Loading rewards for ticket:', {
        userId: ticket.userId,
        queueId: ticket.queueId,
        clientName: ticket.clientName,
      });

      if (!ticket.userId) {
        console.warn('⚠️ [PaymentModal] No userId in ticket!');
        setLoadingRewards(false);
        return;
      }

      try {
        // First, try to get ALL rewards for this user (no status filter)
        const allRewardsQuery = query(
          collection(db, 'loyalty_rewards'),
          where('userId', '==', ticket.userId)
        );

        console.log('🔍 [PaymentModal] Querying ALL rewards with userId:', ticket.userId);

        const allSnapshot = await getDocs(allRewardsQuery);
        console.log('📊 [PaymentModal] Found', allSnapshot.size, 'total rewards for user');

        // Log all rewards found
        allSnapshot.forEach(doc => {
          console.log('🎫 [PaymentModal] Found reward:', {
            id: doc.id,
            ...doc.data(),
          });
        });

        // Now query with status filter
        const rewardsQuery = query(
          collection(db, 'loyalty_rewards'),
          where('userId', '==', ticket.userId),
          where('status', 'in', ['active', 'generated'])
        );

        const snapshot = await getDocs(rewardsQuery);
        console.log('📊 [PaymentModal] Found', snapshot.size, 'active/generated rewards');

        const rewards: LoyaltyReward[] = [];

        snapshot.forEach(doc => {
          const data = doc.data();
          console.log('🎫 [PaymentModal] Checking reward:', {
            id: doc.id,
            code: data.code,
            status: data.status,
            expiresAt: data.expiresAt?.toDate(),
          });

          // Check if not expired
          if (data.expiresAt) {
            const expiryDate = data.expiresAt.toDate();
            if (expiryDate < new Date()) {
              console.log('❌ [PaymentModal] Reward expired:', doc.id);
              return; // Skip expired rewards
            }
          }

          rewards.push({
            rewardId: doc.id,
            ...data,
          } as LoyaltyReward);
        });

        console.log('✅ [PaymentModal] Available rewards:', rewards.length);
        setAvailableRewards(rewards);

        // Auto-select first reward if available
        if (rewards.length > 0) {
          setSelectedRewardId(rewards[0].rewardId);
          console.log('👆 [PaymentModal] Auto-selected reward:', rewards[0].rewardId);
        }
      } catch (err) {
        console.error('❌ [PaymentModal] Error loading client rewards:', err);
      } finally {
        setLoadingRewards(false);
      }
    }

    loadClientRewards();
  }, [ticket.userId]);

  const paymentMethods = [
    { value: 'card' as const, label: 'Tarjeta', icon: CreditCard, color: 'blue' },
    { value: 'cash' as const, label: 'Efectivo', icon: Wallet, color: 'green' },
    { value: 'transfer' as const, label: 'Transferencia', icon: Building2, color: 'purple' },
    { value: 'bizum' as const, label: 'Bizum', icon: Smartphone, color: 'pink' },
  ];

  const handlePriceChange = (value: string) => {
    // Only update the input display, don't modify servicePrice until saved
    setCustomPriceInput(value);
  };

  const handleSavePrice = () => {
    const numValue = parseFloat(customPriceInput.replace(',', '.'));
    if (!isNaN(numValue) && numValue >= 0) {
      const newPriceInCents = Math.round(numValue * 100);
      setServicePrice(newPriceInCents);
      setCustomPriceInput((newPriceInCents / 100).toFixed(2)); // Normalize display
      // Mark as explicitly saved only if different from original
      if (newPriceInCents !== originalServicePrice) {
        setPriceExplicitlySaved(true);
      }
      setIsEditingPrice(false);
    } else {
      // Reset to original service price if invalid (not current, which might be corrupted)
      setServicePrice(originalServicePrice);
      setCustomPriceInput((originalServicePrice / 100).toFixed(2));
      setPriceExplicitlySaved(false);
      setIsEditingPrice(false);
    }
  };

  const handleCancelPriceEdit = () => {
    // If price wasn't explicitly saved yet, restore to original
    if (!priceExplicitlySaved) {
      setServicePrice(originalServicePrice);
      setCustomPriceInput((originalServicePrice / 100).toFixed(2));
    } else {
      // Otherwise just restore the input to match current servicePrice
      setCustomPriceInput((servicePrice / 100).toFixed(2));
    }
    setIsEditingPrice(false);
  };

  const handleResetPrice = () => {
    setServicePrice(originalServicePrice);
    setCustomPriceInput((originalServicePrice / 100).toFixed(2));
    setPriceExplicitlySaved(false);
    setIsEditingPrice(false);
  };

  const handleConfirm = async () => {
    // Auto-save price if user is still in editing mode
    if (isEditingPrice) {
      const numValue = parseFloat(customPriceInput.replace(',', '.'));
      if (!isNaN(numValue) && numValue >= 0) {
        const newPriceInCents = Math.round(numValue * 100);
        setServicePrice(newPriceInCents);
        if (newPriceInCents !== originalServicePrice) {
          setPriceExplicitlySaved(true);
        }
        setIsEditingPrice(false);
      }
    }

    if (!duration || isNaN(parseInt(duration))) {
      setError('Por favor ingresa una duración válida');
      return;
    }

    if (applyVoucher && !selectedRewardId) {
      setError('Por favor selecciona un vale');
      return;
    }

    // Recalculate priceWasModified with potentially auto-saved values
    const numValue = parseFloat(customPriceInput.replace(',', '.'));
    const currentPriceInCents = !isNaN(numValue) && numValue >= 0 ? Math.round(numValue * 100) : servicePrice;
    const effectivePriceModified = currentPriceInCents !== originalServicePrice;
    const effectiveServicePrice = effectivePriceModified ? currentPriceInCents : servicePrice;

    // If price was modified, require a note explaining why
    if (effectivePriceModified && !notes.trim()) {
      setError('Debes añadir una nota explicando el cambio de precio');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 [PaymentModal] Starting payment process for ticket:', ticket.queueId, 'Status:', ticket.status);

      // Step 1: Ensure ticket is in a valid state to complete
      if (ticket.status !== 'completed') {
        // If ticket is not in a state that can be completed, start service first
        if (ticket.status !== 'in_service' && ticket.status !== 'finishing' && ticket.status !== 'arrived') {
          console.log('🔄 [PaymentModal] Ticket is in', ticket.status, 'state. Starting service first...');
          const startServiceFn = httpsCallable(functions, 'startService');
          await startServiceFn({ queueId: ticket.queueId });
          console.log('✅ [PaymentModal] Service started');
        }

        // Now complete the ticket
        console.log('🔄 [PaymentModal] Completing ticket...');
        try {
          const completeTicketFn = httpsCallable(functions, 'completeTicket');
          await completeTicketFn({ queueId: ticket.queueId });
          console.log('✅ [PaymentModal] Ticket completed');
        } catch (completeErr: any) {
          // If ticket is already completed, that's fine - continue with payment
          if (completeErr.message && completeErr.message.includes('Cannot complete ticket from status: completed')) {
            console.log('ℹ️ [PaymentModal] Ticket already completed, continuing...');
          } else {
            // Re-throw other errors
            throw completeErr;
          }
        }
      }

      // Step 2: Record payment with optional reward
      console.log('💰 [PaymentModal] Recording payment...', {
        effectiveServicePrice,
        effectivePriceModified,
        originalServicePrice,
      });
      const recordPaymentFn = httpsCallable(functions, 'recordPayment');
      await recordPaymentFn({
        queueId: ticket.queueId,
        paymentMethod,
        serviceDuration: parseInt(duration),
        notes: notes.trim() || undefined,
        rewardId: applyVoucher ? selectedRewardId : undefined,
        // Send custom price if modified
        customServicePrice: effectivePriceModified ? effectiveServicePrice : undefined,
        originalServicePrice: effectivePriceModified ? originalServicePrice : undefined,
      });

      console.log('✅ [PaymentModal] Payment recorded successfully');
      toast.success('💰 Pago registrado correctamente');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('❌ [PaymentModal] Error:', err);
      setError(err.message || 'Error al procesar el pago');
      toast.error(err.message || 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (_color: string, selected: boolean) => {
    // Single accent treatment for all payment methods (see DESIGN.md)
    return selected
      ? 'border-accent bg-accent/10 text-accent'
      : 'border-hairline bg-surface-2 text-ink-muted hover:border-hairline-strong hover:text-ink';
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-50 p-4 pt-4 pb-24 sm:pb-4 overflow-y-auto">
      <div className="bg-surface border border-hairline rounded-2xl shadow-xl max-w-lg w-full max-h-[calc(100vh-120px)] sm:max-h-[90vh] overflow-y-auto my-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <div>
            <h3 className="font-sans text-lg font-semibold text-ink">
              Registrar Pago
            </h3>
            <p className="text-sm text-ink-muted mt-1">
              Servicio completado para {ticket.clientName || ticket.clientEmail || 'Cliente'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-subtle hover:text-ink transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Service Info & Price Section */}
          <div className="bg-surface-2 rounded-lg p-4 border border-hairline">
            <div className="flex items-center gap-2 mb-3">
              <Scissors className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
              <h4 className="text-sm font-semibold text-ink">
                Servicio Realizado
              </h4>
            </div>

            {loadingServicePrice ? (
              <div className="animate-pulse">
                <div className="h-5 bg-surface-3 rounded w-32 mb-2"></div>
                <div className="h-8 bg-surface-3 rounded w-24"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Service Name */}
                <div>
                  <span className="text-lg font-medium text-ink">
                    {serviceName || 'Servicio'}
                  </span>
                </div>

                {/* Price with edit option */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-ink-subtle flex items-center gap-1">
                      <Euro className="w-3 h-3" />
                      Importe a cobrar
                    </label>
                    {!isEditingPrice && (
                      <button
                        type="button"
                        onClick={() => setIsEditingPrice(true)}
                        disabled={loading}
                        className="text-xs text-accent hover:underline flex items-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Modificar
                      </button>
                    )}
                  </div>

                  {isEditingPrice ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={customPriceInput}
                            onChange={(e) => handlePriceChange(e.target.value)}
                            className="w-full px-3 py-2 pr-8 border border-hairline rounded-lg bg-surface-3 text-ink text-lg font-semibold focus:ring-2 focus:ring-focus focus:border-transparent"
                            disabled={loading}
                            autoFocus
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle font-medium">
                            €
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSavePrice}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-on-accent bg-accent hover:bg-accent-hover rounded-lg transition-colors"
                          disabled={loading}
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelPriceEdit}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-ink bg-surface-3 border border-hairline hover:bg-surface rounded-lg transition-colors"
                          disabled={loading}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-semibold text-ink tabular-nums">
                        {(servicePrice / 100).toFixed(2)}€
                      </span>
                      {priceWasModified && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-ink-subtle line-through tabular-nums">
                            {(originalServicePrice / 100).toFixed(2)}€
                          </span>
                          <button
                            type="button"
                            onClick={handleResetPrice}
                            className="text-xs text-ink-muted hover:text-ink underline"
                            disabled={loading}
                          >
                            Restaurar
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Warning when price is modified */}
                  {priceWasModified && !isEditingPrice && (
                    <div className="mt-2 flex items-start gap-2 p-2 bg-warning/10 border border-warning/30 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-ink">
                        <strong>Precio modificado.</strong> Debes añadir una nota explicando el motivo del cambio.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Available Vouchers Section */}
          {loadingRewards ? (
            <div className="bg-surface-2 border border-hairline rounded-lg p-4">
              <p className="text-sm text-ink-muted">Cargando vales disponibles…</p>
            </div>
          ) : availableRewards.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-3">
                <Gift className="w-4 h-4 inline mr-2 text-ink-subtle" />
                Vales Disponibles del Cliente
              </label>

              {/* Toggle to apply voucher */}
              <div className="bg-accent/5 border border-accent/30 rounded-lg p-4 mb-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyVoucher}
                    onChange={(e) => setApplyVoucher(e.target.checked)}
                    className="w-5 h-5 accent-accent rounded focus:ring-focus"
                    disabled={loading}
                  />
                  <span className="text-sm font-semibold text-ink">
                    Aplicar vale de descuento ({selectedReward && (selectedReward as any).discountType === 'percentage'
                      ? `${(selectedReward as any).discountPercentage}%`
                      : `${(voucherValue / 100).toFixed(0)}€`})
                  </span>
                </label>
              </div>

              {/* Voucher selection */}
              {applyVoucher && (
                <div className="space-y-2 mb-4">
                  {availableRewards.map(reward => {
                    const selected = selectedRewardId === reward.rewardId;
                    return (
                      <button
                        key={reward.rewardId}
                        type="button"
                        onClick={() => setSelectedRewardId(reward.rewardId)}
                        disabled={loading}
                        className={`w-full p-3 rounded-lg border transition-colors text-left ${
                          selected
                            ? 'border-accent bg-accent/10'
                            : 'border-hairline bg-surface-2 hover:border-hairline-strong'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Gift className="w-4 h-4 text-ink-subtle" />
                            <span className="text-sm font-medium text-ink tabular-nums">
                              Vale {(((reward as any).valueCents || (reward as any).value || ((reward as any).valueInEuros ? (reward as any).valueInEuros * 100 : 1000)) / 100).toFixed(0)}€
                            </span>
                            <span className="text-xs text-ink-subtle">
                              Código: {reward.code}
                            </span>
                          </div>
                          {selected && (
                            <Check className="w-5 h-5 text-accent" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Price Breakdown */}
              {applyVoucher && (
                <div className="bg-surface-2 rounded-lg p-4 border border-hairline">
                  <h4 className="text-sm font-semibold text-ink mb-3">
                    Desglose del Pago
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-ink-muted">Precio del servicio:</span>
                      <span className="text-sm font-semibold text-ink tabular-nums">
                        {(servicePrice / 100).toFixed(2)}€
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-ink-muted">Descuento (vale):</span>
                      <span className="text-sm font-semibold text-success tabular-nums">
                        -{(discountAmount / 100).toFixed(2)}€
                      </span>
                    </div>
                    <div className="border-t border-hairline pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-ink">
                          {voucherCoversAll ? 'Servicio cubierto por vale' : 'Resta cobrar:'}
                        </span>
                        <span className="text-2xl font-semibold text-ink tabular-nums">
                          {(remainingAmount / 100).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-surface-2 border border-hairline rounded-lg p-4">
              <p className="text-sm text-ink-muted">
                Este cliente no tiene vales disponibles
              </p>
            </div>
          )}

          {/* Payment Method - Only show if there's remaining amount or no voucher applied */}
          {(!applyVoucher || !voucherCoversAll) && (
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-3">
                {applyVoucher ? `Método de Pago (${(remainingAmount / 100).toFixed(2)}€)` : 'Método de Pago'}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {paymentMethods.map(method => {
                  const Icon = method.icon;
                  const selected = paymentMethod === method.value;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => {
                        setPaymentMethod(method.value);
                        setError(null);
                      }}
                      className={`p-3 rounded-lg border transition-colors ${getColorClasses(
                        method.color,
                        selected
                      )}`}
                      disabled={loading}
                    >
                      <Icon className="w-6 h-6 mx-auto mb-1" />
                      <span className="text-xs font-medium">{method.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary for combined payment */}
          {applyVoucher && !voucherCoversAll && (
            <div className="bg-surface-2 border border-hairline rounded-lg p-3">
              <p className="text-xs text-ink-muted">
                <strong className="text-ink">Pago combinado:</strong> {(discountAmount / 100).toFixed(2)}€ con vale + {(remainingAmount / 100).toFixed(2)}€ restantes en {paymentMethods.find(m => m.value === paymentMethod)?.label.toLowerCase()}
              </p>
            </div>
          )}

          {/* Duration */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-ink-muted">
                <Clock className="w-4 h-4 inline mr-2 text-ink-subtle" />
                Duración (minutos)
              </label>
              {!isEditingDuration && duration && (
                <button
                  type="button"
                  onClick={() => setIsEditingDuration(true)}
                  disabled={loading}
                  className="text-xs text-accent hover:underline flex items-center gap-1"
                >
                  <Edit2 className="w-3 h-3" />
                  Ajustar
                </button>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                value={duration}
                onChange={(e) => {
                  setDuration(e.target.value);
                  setError(null);
                }}
                min="1"
                max="300"
                readOnly={!isEditingDuration}
                className={`w-full px-4 py-2 border rounded-lg text-ink focus:ring-2 focus:ring-focus focus:border-transparent ${
                  isEditingDuration
                    ? 'border-hairline bg-surface-3'
                    : 'border-hairline bg-surface-2 cursor-default'
                }`}
                disabled={loading}
              />
              {!isEditingDuration && duration && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="text-xs text-ink-subtle font-medium bg-surface-3 px-2 py-0.5 rounded">
                    Auto-calculado
                  </span>
                </div>
              )}
            </div>
            {!isEditingDuration && duration && (
              <p className="text-xs text-ink-subtle mt-1">
                Tiempo calculado automáticamente. Haz clic en "Ajustar" si necesitas modificarlo.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {priceWasModified ? (
                <span className="flex items-center gap-2">
                  Motivo del cambio de precio
                  <span className="text-danger text-xs font-normal">(obligatorio)</span>
                </span>
              ) : (
                'Notas (opcional)'
              )}
            </label>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setError(null);
              }}
              placeholder={priceWasModified
                ? "Ej: Cliente pidió un corte más sencillo, se ajusta el precio..."
                : "Añade cualquier información adicional..."
              }
              rows={priceWasModified ? 3 : 2}
              maxLength={200}
              className={`w-full px-4 py-2 border rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent resize-none ${
                priceWasModified && !notes.trim()
                  ? 'border-warning/50 ring-1 ring-warning/30'
                  : 'border-hairline'
              }`}
              disabled={loading}
            />
            <p className="text-xs text-ink-subtle mt-1 tabular-nums">
              {notes.length}/200 caracteres
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-ink">{error}</p>
            </div>
          )}
        </div>

        {/* Footer - Sticky on mobile to ensure buttons are always visible */}
        <div className="sticky bottom-0 flex gap-3 p-4 sm:p-6 border-t border-hairline bg-surface">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || loadingServicePrice || loadingRewards}
            className="flex-1 py-3"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-accent mr-2 inline-block"></div>
                Guardando…
              </>
            ) : loadingServicePrice || loadingRewards ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-accent mr-2 inline-block"></div>
                Cargando…
              </>
            ) : (
              'Confirmar Pago'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
